import React, { useState, useRef, JSX } from 'react';
import { Box } from '@mui/material';
import { BoardState, Position } from '../model/BoardState';
import Pawn from '../piece/Pawn';
import Rook from '../piece/Rook';
import Knight from '../piece/Knight';
import Bishop from '../piece/Bishop';
import Queen from '../piece/Queen';
import King from '../piece/King';
import { Piece } from '../model/Piece';
import logger from '../utils/Logger';

interface ChessboardProps {
  boardSize?: number;
  boardState: BoardState;
  handlePlayerMoveCallback: (from: Position, to: Position) => void;
  highlightedSquare: Position | null;
  highlightedComputerSquare: Position | null;
  playerColor: { white: string; black: string };
  currentTurn: 'Player' | 'Computer';
  highlightedLegalMoves: Position[];
  setHighlightedLegalMoves: (positions: Position[]) => void;
  isGameOver: boolean;
}

let triggerComputerMoveAnimation: (from: Position, to: Position) => Promise<void>;

const VintageChessBoard: React.FC<ChessboardProps> = ({
                                                        boardSize = 1024,
                                                        boardState,
                                                        handlePlayerMoveCallback,
                                                        highlightedSquare,
                                                        highlightedComputerSquare,
                                                        playerColor,
                                                        currentTurn,
                                                        highlightedLegalMoves,
                                                        setHighlightedLegalMoves,
                                                        isGameOver,
                                                      }) => {
  const [draggedPiece, setDraggedPiece] = useState<Position | null>(null);
  const [animatedPiece, setAnimatedPiece] = useState<{ piece: JSX.Element; from: Position; to: Position } | null>(null);

  const boardRef = useRef<HTMLDivElement | null>(null);
  const squareSize = (boardSize * 0.78) / 8;

  const animatePiece = (from: Position, to: Position, duration: number) => {
    // Find the piece at the 'from' position in the board state
    const piece = boardState.pieces.find(p => p.position.row === from.row && p.position.col === from.col);
    if (!piece) {
      logger.error('Piece not found for:', JSON.stringify(from));
      return;
    }

    // Create a JSX element representing the piece to be animated
    const pieceElement = (
      <div
        style={{
          position: 'absolute', 
          width: squareSize,
          height: squareSize,
          // Apply the same positioning logic as the grid (right: 7%, top: 11%)
          right: `calc(7% + ${(7 - from.col) * squareSize}px)`,
          top: `calc(11% + ${from.row * squareSize}px)`,
          color: piece.color === 'white' ? playerColor.white : playerColor.black,
          transition: `right ${duration}ms linear, top ${duration}ms linear`,
        }}
      >
        {piece.type === 'pawn' && <Pawn pieceColor={piece.color} />}
        {piece.type === 'rook' && <Rook pieceColor={piece.color} />}
        {piece.type === 'knight' && <Knight pieceColor={piece.color} />}
        {piece.type === 'bishop' && <Bishop pieceColor={piece.color} />}
        {piece.type === 'queen' && <Queen pieceColor={piece.color} />}
        {piece.type === 'king' && <King pieceColor={piece.color} />}
      </div>
    );

    setAnimatedPiece({ piece: pieceElement, from, to });

    // Trigger the animation by updating positions after a short delay
    setTimeout(() => {
      setAnimatedPiece(prev => {
        if (!prev) return null;
        return {
          ...prev,
          piece: React.cloneElement(prev.piece, {
            style: {
              ...prev.piece.props.style,
              right: `calc(7% + ${(7 - to.col) * squareSize}px)`,
              top: `calc(11% + ${to.row * squareSize}px)`,
            },
          }),
        };
      });
    }, 50);

    // Clear the animated piece state after the animation duration
    setTimeout(() => {
      setAnimatedPiece(null);
    }, duration);
  };

  triggerComputerMoveAnimation = (from: Position, to: Position): Promise<void> => {
    return new Promise(resolve => {
      const duration = 1000;
      animatePiece(from, to, duration);

      setTimeout(() => {
        resolve();
      }, duration);
    });
  };

  const handleDragStart = (position: Position) => {
    if (isGameOver || currentTurn !== 'Player') {
      return;
    }
    const piece = boardState.pieces.find(p => p.position.row === position.row && p.position.col === position.col);
    if (piece && piece.color === 'black') {
      setDraggedPiece(position);
      const legalMoves = boardState.getLegalMoves(piece);
      setHighlightedLegalMoves(legalMoves);
    }
  };

  const handleDrop = (position: Position) => {
    if (draggedPiece) {
      const piece = boardState.pieces.find(p => p.position.row === draggedPiece.row && p.position.col === draggedPiece.col);
      if (piece) {
        const legalMoves = boardState.getLegalMoves(piece);
        if (legalMoves.some(move => move.row === position.row && move.col === position.col)) {
          handlePlayerMoveCallback(draggedPiece, position);
          const beep = new Audio('/sounds/chess-move-click.wav');
          beep.play();
        }
      }
      setDraggedPiece(null);
      setHighlightedLegalMoves([]);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleMouseDown = (position: Position) => {
    if (isGameOver || currentTurn !== 'Player') {
      return;
    }
    const piece = boardState.pieces.find(p => p.position.row === position.row && p.position.col === position.col);
    if (piece && piece.color === 'black') {
      const legalMoves = boardState.getLegalMoves(piece);
      setHighlightedLegalMoves(legalMoves);
    }
  };

  const handleMouseUp = () => {
    setHighlightedLegalMoves([]);
  };

  const shouldRenderPiece = (
    piece: Piece | undefined,
    animatedPiece: { from: Position; to: Position } | null,
    row: number,
    col: number
  ): boolean => {
    if (!piece) return false;
    if (!animatedPiece) return true;

    const isAnimatingFromCell = animatedPiece.from.row === row && animatedPiece.from.col === col;
    const isAnimatingToCell = animatedPiece.to.row === row && animatedPiece.to.col === col;

    if (isAnimatingToCell && piece.color === 'black') return true;
    if (isAnimatingToCell) return false;

    return !isAnimatingFromCell;
  };

  const squares: JSX.Element[] = [];
  for (let row = 0; row < 8; row++) {
    for (let col = 0; col < 8; col++) {
      const isHighlighted = highlightedSquare && highlightedSquare.row === row && highlightedSquare.col === col;
      const isComputerHighlighted = highlightedComputerSquare && highlightedComputerSquare.row === row && highlightedComputerSquare.col === col;
      const isLegalMoveHighlighted = highlightedLegalMoves.some(move => move.row === row && move.col === col);

      const piece = boardState.pieces.find(p => p.position.row === row && p.position.col === col);

      squares.push(
        <Box
          key={`${row}-${col}`}
          sx={{
            width: squareSize,
            height: squareSize,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            boxSizing: 'border-box',
            borderColor: isHighlighted || isComputerHighlighted ? 'red' : isLegalMoveHighlighted ? 'blue' : 'transparent',
            borderWidth: isHighlighted || isComputerHighlighted ? '3px' : isLegalMoveHighlighted ? '3px' : '0px',
            borderStyle: isHighlighted || isComputerHighlighted ? 'solid' : isLegalMoveHighlighted ? 'dotted' : 'none',
          }}
          onDragOver={handleDragOver}
          onDrop={() => handleDrop({ row, col })}
          onMouseUp={handleMouseUp}
        >
          {shouldRenderPiece(piece, animatedPiece, row, col) && (
            <div
              draggable={piece?.color === 'black'}
              onDragStart={() => handleDragStart({ row, col })}
              onMouseDown={() => handleMouseDown({ row, col })}
              onMouseUp={handleMouseUp}
              style={{
                width: '100%',
                height: '100%',
                color: piece?.color === 'white' ? playerColor.white : playerColor.black,
              }}
              role="button"
              tabIndex={0}
              aria-label={`${piece?.color} ${piece?.type} at position ${row},${col}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleDragStart({ row, col });
                }
              }}
            >
              {piece?.type === 'pawn' && <Pawn pieceColor={piece.color} />}
              {piece?.type === 'rook' && <Rook pieceColor={piece.color} />}
              {piece?.type === 'knight' && <Knight pieceColor={piece.color} />}
              {piece?.type === 'bishop' && <Bishop pieceColor={piece.color} />}
              {piece?.type === 'queen' && <Queen pieceColor={piece.color} />}
              {piece?.type === 'king' && <King pieceColor={piece.color} />}
            </div>
          )}
        </Box>
      );
    }
  }

  return (
    <Box ref={boardRef} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', position: 'relative' }}>
      <Box
        sx={{
          width: '125%',
          height: '100%',
          backgroundImage: 'url(/images/board-background.jpg)',
          backgroundSize: '92%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          position: 'absolute',
          left: '-20%',
          zIndex: 0,
        }}
      />
      <Box sx={{ display: 'block', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', position: 'relative' }}>
        <Box
          sx={{
            width: '95%',
            height: '78%',
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 1fr)',
            gridTemplateRows: 'repeat(8, 1fr)',
            gap: 0,
            position: 'relative',
            right: '7%',
            top: '11%',
            zIndex: 1,
          }}
        >
          {squares}
        </Box>
        {animatedPiece && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            {animatedPiece.piece}
          </div>
        )}
      </Box>
    </Box>
  );
};

export { triggerComputerMoveAnimation };
export default VintageChessBoard;
